import { useRef } from 'react';

import type { LngLat, YMap } from '@yandex/ymaps3-types';
import type { Feature } from '@yandex/ymaps3-types/packages/clusterer';

import { MapLoader } from '../../../shared/components/MapLoader';
import {
  Cluster,
  Hint,
  LocationMarker,
  YandexMap,
  YandexMapAutocomplete,
  YandexMapProvider,
  useYandexMap,
} from '../../../shared/components/YandexMap';
import type { CustomFeatureLocation } from '../lib/normalizeYandexMapData';
import { useNormalizeYandexMapData } from '../lib/normalizeYandexMapData';
import { useLocationMap } from '../model/LocationMapDataProvider';
import { AutocompleteBox } from './AutocompleteBox';

const SOURCE = 'source';

const YandexMapLocationBase = () => {
  const ymapRef = useRef<YMap>(null);
  const data = useLocationMap();
  const { ymapsInstance } = useYandexMap();
  const {
    zoom,
    center,
    locations,
    onMarkerClick,
    onMapClick,
    searchLocation,
    onChangeSearchLocation,
    onDraggableMarker,
  } = useNormalizeYandexMapData(data, ymapRef);

  if (!ymapsInstance) {
    return <MapLoader />;
  }

  const { YMapListener, YMapDefaultSchemeLayer, YMapFeatureDataSource, YMapLayer } = ymapsInstance.ymaps;
  const { YMapClusterer, clusterByGrid } = ymapsInstance.clusterer;

  const locationMarker = (feature: Feature) => {
    const updateFeature = feature as CustomFeatureLocation;
    return (
      <LocationMarker
        view='primary'
        draggable
        ymapsInstance={ymapsInstance}
        coordinates={updateFeature.geometry.coordinates}
        source={SOURCE}
        onClick={onMarkerClick(updateFeature.properties)}
        properties={{
          hint: updateFeature.properties.hint,
        }}
        onDragEnd={onDraggableMarker(updateFeature.properties)}
      />
    );
  };

  const handleClusterClick = (coordinates: LngLat) => () => {
    ymapRef.current?.setLocation({
      center: coordinates,
      zoom: zoom + 2,
      duration: 300,
    });
  };

  const cluster = (coordinates: LngLat, features: Feature[]) => (
    <Cluster
      count={features.length}
      ymapsInstance={ymapsInstance}
      coordinates={coordinates}
      source={SOURCE}
      onClick={handleClusterClick(coordinates)}
    />
  );

  const method = clusterByGrid({ gridSize: 64 });

  return (
    <YandexMap ref={ymapRef} location={{ zoom, center }}>
      <YMapDefaultSchemeLayer />
      <YMapFeatureDataSource id={SOURCE} />
      <YMapLayer source={SOURCE} type='markers' zIndex={1800} />
      <YMapListener onClick={onMapClick} />
      <Hint ymapsInstance={ymapsInstance} />
      {searchLocation && <LocationMarker ymapsInstance={ymapsInstance} coordinates={searchLocation} />}
      <YMapClusterer method={method} features={locations} marker={locationMarker} cluster={cluster} />
      <AutocompleteBox>
        <YandexMapAutocomplete ymapsInstance={ymapsInstance} onChange={onChangeSearchLocation} />
      </AutocompleteBox>
    </YandexMap>
  );
};

export const YandexMapLocation = () => (
  <YandexMapProvider>
    <YandexMapLocationBase />
  </YandexMapProvider>
);
